import { Component, ViewChild, inject } from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  GsEventSelectorComponent,
  SelectionMeta,
} from 'src/app/shared/features/event-selector/gs-event-selector.component';
import { UtilsService } from 'src/app/private/services/utils.service';
import { DialogCloseReason } from '../../../layout/dialog/app-dialog.service';
import { Discount } from '../../models/discount.model';
import { DiscountsService } from '../../services/discounts.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-discount-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.css'],
  imports: [
    BoDialogComponent,
    ButtonComponent,
    FormsModule,
    GsEventSelectorComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideX })],
})
export class DiscountCreateComponent {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private discountsService = inject(DiscountsService);
  private utils = inject(UtilsService);
  private toastr = inject(ToastrService);

  @ViewChild(GsEventSelectorComponent)
  eventSelector: GsEventSelectorComponent;

  public item: Discount;
  public selectedEventIds: number[] = [];
  public isAllEventsSelected = false;
  public hashValidator = {
    code: false,
    description: false,
    discountPercentage: false,
  };

  constructor() {
    this.item = {
      id: 0,
      code: '',
      description: '',
      discountPercentage: 0,
      enabled: true,
      maxUses: null,
      minBalance: null,
      maxBalance: null,
      validFrom: null,
      validUntil: null,
    };
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    if (this.checkInputs(this.item)) return;
    const itemToSave = {
      ...this.item,
      validFrom: this.item.validFrom
        ? this.toUtcDatetime(this.item.validFrom)
        : null,
      validUntil: this.item.validUntil
        ? this.toUtcDatetime(this.item.validUntil)
        : null,
    };
    this.discountsService
      .create$(itemToSave, this.selectedEventIds ?? [])
      .subscribe({
        next: () => {
          this.onDismiss('success');
        },
        error: err => {
          this.toastr.error(this.getApiErrorMessage(err));
        },
      });
  }

  private checkInputs(instance: Discount): boolean {
    let hasErrors = false;
    Object.keys(this.hashValidator).forEach(value => {
      (this.hashValidator as any)[value] = false;
    });
    if (this.utils.isEmpty(instance.code)) {
      this.hashValidator.code = true;
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.description)) {
      this.hashValidator.description = true;
      hasErrors = true;
    }
    if (
      instance.discountPercentage <= 0 ||
      instance.discountPercentage >= 100
    ) {
      this.hashValidator.discountPercentage = true;
      hasErrors = true;
    }
    return hasErrors;
  }

  public onEventSelectionChange(ids: number[]) {
    this.selectedEventIds = ids;
  }

  public onEventSelectionMeta(meta: SelectionMeta) {
    this.isAllEventsSelected = meta.isAllSelected;
  }

  public onSelectAllEventsChange(event: any) {
    this.eventSelector.selectAll(event.target.checked);
  }

  private getApiErrorMessage(err: unknown): string {
    const errorBody = (err as { error?: Record<string, unknown> })?.error;
    const error = errorBody?.['error'] as
      | {
          message?: string;
          data?: Array<{ message?: string; path?: string[] }>;
        }
      | undefined;

    if (error?.data && Array.isArray(error.data) && error.data.length > 0) {
      const firstError = error.data[0];
      const path = firstError.path?.join('.') || 'field';
      return `${path}: ${firstError.message || 'Invalid value'}`;
    }

    if (
      Array.isArray(errorBody?.['errors']) &&
      errorBody['errors'].length > 0
    ) {
      return String(
        (errorBody['errors'][0] as { message?: string }).message ??
          'An error occurred'
      );
    }

    if (error?.message) {
      return error.message;
    }

    if (typeof errorBody?.['message'] === 'string') {
      return errorBody['message'];
    }

    return 'An error occurred while saving the discount.';
  }

  private toUtcDatetime(localString: string): string {
    const date = new Date(localString);
    return date.toISOString();
  }
}
